import { Badge } from "@/components/ui/badge";
import { LEVEL_BADGE_CLASSES } from "@/lib/level-badge";
import { HAZARD_LABELS } from "@/lib/results";
import { cn } from "@/lib/utils";
import { ASSET_TYPE_VULNERABILITY_DEFAULTS } from "@shared/seed-data/asset-type-vulnerability-defaults";
import { HAZARD_TYPES, type HazardType } from "@shared/scoring/types";
import type { WorkspaceSector } from "@/lib/auth";

/** The seed file keys hazards by display name, not by HazardType id. */
const HAZARD_BY_SEED_NAME = new Map<string, HazardType>(
  HAZARD_TYPES.map((hazard) => [HAZARD_LABELS[hazard], hazard]),
);

interface Row {
  assetTypeName: string;
  scores: Partial<Record<HazardType, number>>;
  spread: number;
}

function buildRows(sector: WorkspaceSector): Row[] {
  const byAssetType = new Map<string, Partial<Record<HazardType, number>>>();

  for (const entry of ASSET_TYPE_VULNERABILITY_DEFAULTS) {
    if (entry.sector !== sector) continue;
    const hazard = HAZARD_BY_SEED_NAME.get(entry.hazard);
    if (!hazard) continue; // a hazard name the app doesn't render — skip rather than guess
    const scores = byAssetType.get(entry.assetTypeName) ?? {};
    scores[hazard] = entry.score;
    byAssetType.set(entry.assetTypeName, scores);
  }

  return Array.from(byAssetType, ([assetTypeName, scores]) => {
    const values = Object.values(scores).filter((v): v is number => v !== undefined);
    return {
      assetTypeName,
      scores,
      spread: values.length ? Math.max(...values) - Math.min(...values) : 0,
    };
  });
}

/**
 * Real reference data from shared/seed-data/asset-type-vulnerability-defaults.ts,
 * which was transcribed programmatically from the client's Vulnerability
 * sheet — never hand-entered here.
 *
 * No "stage" column: asset types carry no value-chain-stage mapping in this
 * codebase (asset-types.ts sets valueChainStageId to null throughout, and
 * that mapping is functional-spec open items #4/#5). A column of blanks would
 * imply the mapping exists.
 */
export function VulnerabilityByAssetTypeTable({ sector }: { sector: WorkspaceSector }) {
  const rows = buildRows(sector);

  if (rows.length === 0) {
    return <p className="p-5 text-sm text-muted-foreground">No reference scores for this sector yet.</p>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-xs">
        <thead>
          <tr className="border-b border-border bg-background/60 text-xs font-bold uppercase tracking-[0.08em] text-muted-foreground">
            <th className="px-4 py-3">Asset type</th>
            {HAZARD_TYPES.map((hazard) => (
              <th key={hazard} className="px-3 py-3 text-center">
                {HAZARD_LABELS[hazard]}
              </th>
            ))}
            <th className="px-4 py-3 text-center">Spread</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.assetTypeName} className="border-t border-border/60">
              <td className="px-4 py-3 font-bold">{row.assetTypeName}</td>
              {HAZARD_TYPES.map((hazard) => {
                const score = row.scores[hazard];
                return (
                  <td key={hazard} className="px-3 py-3 text-center">
                    {score === undefined ? (
                      <span className="text-muted-foreground">—</span>
                    ) : score === 0 ? (
                      // 0 is "no physical impact", outside the 1-4 level scale,
                      // so it gets a neutral chip rather than a level color.
                      <Badge className="border-border bg-muted text-muted-foreground">0</Badge>
                    ) : (
                      <Badge className={LEVEL_BADGE_CLASSES[score as 1 | 2 | 3 | 4]}>{score}</Badge>
                    )}
                  </td>
                );
              })}
              <td
                className={cn(
                  "px-4 py-3 text-center",
                  row.spread === 0 ? "font-bold text-amber-700" : "text-muted-foreground",
                )}
              >
                {row.spread === 0 ? "none" : row.spread}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="border-t border-border px-4 py-3 text-xs leading-5 text-muted-foreground">
        Spread is the gap between an asset type's highest and lowest hazard score — how much this asset type
        distinguishes between hazards before exposure is applied.
      </p>
    </div>
  );
}
